import React, { useState, useEffect, useRef } from 'react';
import { login, googleLoginWithCode } from '../../services';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isGoogleScriptLoaded, setIsGoogleScriptLoaded] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const codeClientRef = useRef(null);
  const passwordInputRef = useRef(null);
  
  // Get the redirect path from location state, default to home
  const from = location.state?.from?.pathname || '/';

  // Load Google Identity Services SDK
  useEffect(() => {
    const loadGoogleScript = () => {
      // Check if script is already loaded and Google object exists
      if (window.google && window.google.accounts) {
        setIsGoogleScriptLoaded(true);
        return;
      }

      // Check if script tag exists but Google object isn't ready yet
      if (document.querySelector('script#google-identity-script')) {
        // Create a timer to check for Google object availability
        const checkGoogleExists = setInterval(() => {
          if (window.google && window.google.accounts) {
            setIsGoogleScriptLoaded(true);
            clearInterval(checkGoogleExists);
          }
        }, 100);
        
        // Safety timeout after 5 seconds
        setTimeout(() => clearInterval(checkGoogleExists), 5000);
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.id = 'google-identity-script';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Wait for Google object to become available
        const checkGoogleExists = setInterval(() => {
          if (window.google && window.google.accounts) {
            setIsGoogleScriptLoaded(true);
            clearInterval(checkGoogleExists);
          }
        }, 100);
        
        // Safety timeout after 5 seconds
        setTimeout(() => clearInterval(checkGoogleExists), 5000);
      };
      document.body.appendChild(script);
    };

    loadGoogleScript();

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Initialize GIS OAuth 2.0 Code Client when script is loaded
  useEffect(() => {
    if (isGoogleScriptLoaded && window.google && window.google.accounts && !codeClientRef.current) {
      try {
        codeClientRef.current = window.google.accounts.oauth2.initCodeClient({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          scope: 'openid email',
          ux_mode: 'popup',
          callback: async (resp) => {
            if (resp && resp.code) {
              try {
                await googleLoginWithCode(resp.code);
                navigate(from, { replace: true });
              } catch (error) {
                console.error('Error during Google OAuth login:', error);
                setErrorMessage('Google login failed. Please try again.');
              }
            } else if (resp && resp.error) {
              setErrorMessage('Google login was cancelled or failed.');
            }
          }
        });
      } catch (error) {
        console.error('Error initializing Google OAuth client:', error);
        setErrorMessage('Failed to initialize Google Sign-In');
      }
    }
  }, [isGoogleScriptLoaded, from, navigate]);

  const handleSubmit = async (email, password) => {
    try {
      if (!email || !password) {
        // Basic form validation
        setErrorMessage('All fields are required');
        return;
      }

      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(email)) {
        setErrorMessage('Invalid email address');
        return;
      }

      await login(email, password);
      setEmail('');
      setPassword('');
      navigate(from, { replace: true });
    } catch (error) {
      setErrorMessage('Login failed. Please check your email and password.');
    }
  }

  return (
    <div className="page-background">
      <div className="w-72">
        <h2 className="text-2xl font-bold mb-6 text-center text-white">Log in</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(email, password);
          }}
          className="space-y-4">
          <div>
            <input
              id="email"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              className="credential-input"
            />
          </div>
          <div className="relative">
            <input
              ref={passwordInputRef}
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setShowPassword(false)}
              placeholder="Password"
              className="credential-input pr-10"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => {
                setShowPassword(!showPassword);
                setTimeout(() => {
                  if (passwordInputRef.current) {
                    passwordInputRef.current.focus();
                    passwordInputRef.current.setSelectionRange(password.length, password.length);
                  }
                }, 0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowPassword(!showPassword);
                  setTimeout(() => {
                    if (passwordInputRef.current) {
                      passwordInputRef.current.focus();
                      passwordInputRef.current.setSelectionRange(password.length, password.length);
                    }
                  }, 0);
                }
              }}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              tabIndex={0}
            >
              {showPassword ? (
                <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              ) : (
                <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          </div>
          {errorMessage && (
          <div className="text-red-500 text-sm mt-2 text-center">{errorMessage}</div>
        )}
          <div className="text-center mt-4">
            <button
              type="button"
              className="text-sm text-white hover:underline"
              onClick={() => navigate('/forgot-password')}
            >
              Forgot your password?
            </button>
          </div>
          <button
            type="submit"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-tipl-yellow hover:bg-yellow-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Log in
          </button>
          
          <div className="mt-4 relative">
            <div className="relative flex justify-center">
              <span className="px-2 text-white text-sm">Or</span>
            </div>
          </div>
          
          <div className="mt-4 w-full">
            <button
              type="button"
              className="gsi-material-button w-full"
              onClick={() => codeClientRef.current && codeClientRef.current.requestCode()}
            >
              <div className="gsi-material-button-state"></div>
              <div className="gsi-material-button-content-wrapper">
                <div className="gsi-material-button-icon">
                  <svg
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 48 48"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    style={{ display: 'block' }}
                  >
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                    <path fill="none" d="M0 0h48v48H0z"></path>
                  </svg>
                </div>
                <span className="gsi-material-button-contents">Sign in with Google</span>
                <span style={{ display: 'none' }}>Sign in with Google</span>
              </div>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
